// Runs in the browser TAB, not in the SharedWorker: owns this tab's heartbeat and what the tab does when
// the worker is dead (BEAST-14120).
import { store } from 'store/store';
import { generalActions } from 'store/generalSlice/generalSlice';
import { createSharedWorkerHeartbeat } from 'workers/sharedWorker/utils/sharedWorkerHeartbeat';
import { sharedWorkerMessage } from 'workers/sharedWorker/utils/utils';
import { aiqLogger } from 'services/aiqLogger';

// localStorage signal sent by the tab where the agent clicked "Reload page", so the other tabs recover too.
export const SHARED_WORKER_DEAD_SIGNAL = 'AIQ_SharedWorkerDead';
// Upper bound for writing the buffered logs to the outbox before a reload; it is a local write, so it is
// normally done in milliseconds, and a stuck write must not block the reload.
export const LOG_FLUSH_TIMEOUT_MS = 1000;

let heartbeat = null;
let heartbeatPort = null;
let isReloading = false;

// The port is closed so a worker that was only stalled drops this window's registration (and hands its
// transactions to other tabs). The overlay offers a page reload instead of "Reconnect now": a reconnected
// tab keeps stale module state (e.g. the camera stream tab uid), while the desk is INACTIVE on the server
// anyway and has to be selected again. The heartbeat is stopped so a tab left on the overlay does not
// declare the worker dead again.
export function handleSharedWorkerDead(port) {
    heartbeat?.stop();
    // Explicit removeWindow first: the worker learns about a closed port only through port.onclose, which not
    // every browser supports. Without it, a worker that was only slow (false alarm) would keep this window
    // registered and keep assigning it transactions it can no longer deliver. A dead worker just never gets it.
    port.postMessage(sharedWorkerMessage.removeWindow({}));
    port.close();
    store.dispatch(generalActions.setIsSharedWorkerDead(true));
    store.dispatch(generalActions.setIsWindowDisconnectedFromSharedWorker(true));
}

export function startSharedWorkerHeartbeat(port) {
    heartbeat?.stop();
    heartbeatPort = port;
    heartbeat = createSharedWorkerHeartbeat({
        port,
        onDead: () => handleSharedWorkerDead(port),
    });
    heartbeat.start();
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Writes the last logs to the outbox first, so the lines that explain the reload (dead worker, probe) are
// sent later by whichever tab or worker is alive.
export async function flushLogsThenReload() {
    isReloading = true;
    try {
        await Promise.race([aiqLogger.flush(), sleep(LOG_FLUSH_TIMEOUT_MS)]);
    } catch {
        // Reload anyway.
    }
    window.location.reload();
}

// Another tab reported the worker dead. The worker is shared, so if it is dead here too this tab reloads
// as well; a probe first makes sure one tab's false alarm does not reload healthy tabs. Every tab reloads,
// also one without STOMP subscriptions: the dead-worker overlay blocks the page anyway, so waiting for a
// click would not save anything.
export async function confirmSharedWorkerDeadAndReload() {
    if (isReloading) return;
    if (store.getState().general?.isSharedWorkerDead) {
        await flushLogsThenReload();
        return;
    }
    if (!heartbeat) return;

    isReloading = true;
    const alive = await heartbeat.probe();
    if (alive) {
        isReloading = false;
        console.warn('[SharedWorkerLiveness] Another tab reported the SharedWorker dead, but it answers here. Ignoring.');
        return;
    }
    handleSharedWorkerDead(heartbeatPort);
    console.warn('[SharedWorkerLiveness] Another tab reported the SharedWorker dead and it does not answer here either. Reloading.');
    await flushLogsThenReload();
}

// Test-only: module state survives between tests otherwise.
export function resetSharedWorkerLiveness() {
    heartbeat?.stop();
    heartbeat = null;
    heartbeatPort = null;
    isReloading = false;
}
